import React, { use, useCallback, useEffect, useRef } from "react";
import { View } from "react-native";
import { AuthManagerContext } from "../../contexts/AuthManager";
import { StackScreenProps } from "@react-navigation/stack";
import { RootStackParamList } from "../../navigations/RootNavigation";

import Loading from "../../components/Loading";
import { END_VERIFF_SESSION, VERIFICATION_RESET } from "../../graphql/identity";
import { useMutation } from "@apollo/client/react";
import { useAtomValue } from "jotai";
import { currentLang } from "../../atoms/i18n";
import VeriffSdk from "../../veriff";
import { useTheme } from "../../hooks/use-theme";
import ScreenCardHeadband from "../ScreenCardHeadband";
import { useI18n } from "../../hooks/i18n-hooks";
import { useTracking } from "../../hooks/tracking";

type VeriffScreenProps = StackScreenProps<RootStackParamList, "VeriffScreen">;

/*
replace await VeriffSdk.launchVeriff with await fakeVeriff to bypass the Veriff SDK

const fakeVeriff = async (sessionUrl: string) => {
    return new Promise<{ error: string; status: string; sessionUrl: string }>((resolve) => {
        setTimeout(() => {
            resolve({
                error: "",
                status: VeriffSdk.statusDone,
                sessionUrl,
            });
        }, 1000);
    });
};
*/
const VeriffScreen: React.FC<VeriffScreenProps> = ({ navigation }) => {
    const currentSessionUrlRef = useRef<string>(undefined);
    const [endVeriffSession, { loading, error }] = useMutation(END_VERIFF_SESSION);
    const [resetVeriffSession] = useMutation(VERIFICATION_RESET);

    const { colors } = useTheme();

    const { user, refreshUser } = use(AuthManagerContext);
    const lang = useAtomValue(currentLang);
    const currentUrl = user?.kycStatus?.veriffUrl;
    const i18n = useI18n();
    const { trackEvent } = useTracking();
    const doVerification = useCallback(
        async (sessionUrl: string): Promise<undefined> => {
            const { status, error } = await VeriffSdk.launchVeriff({
                sessionUrl: sessionUrl,
                locale: lang,
                branding: {
                    background: colors.background,
                    onBackground: colors.primary,
                    onBackgroundSecondary: colors.secondary,
                    onBackgroundTertiary: colors.primary,
                    primary: colors.primary,
                    onPrimary: colors.background,
                    secondary: colors.secondary,
                    onSecondary: colors.accentLighter,
                    cameraOverlay: colors.primary,
                    onCameraOverlay: colors.background,
                    buttonRadius: 24,
                    logo: {
                        uri: "https://storage.googleapis.com/lift-public-assets/veriff-liftlogo-white.png",
                    },
                },
            });
            void trackEvent("kyc_completed", { status });
            // eslint-disable-next-line no-console
            console.log(`Veriff.launchVeriff result: status='${status}', error='${error}'`);
            if (status === VeriffSdk.statusDone) {
                // End veriff session will set KYC to submitted status and then resfresh user
                // will navigate to the next screen
                await endVeriffSession();
                await refreshUser?.();
                currentSessionUrlRef.current = undefined;
            } else if (status === VeriffSdk.statusError) {
                if (error === "SESSION_ERROR") {
                    // session expired
                    await resetVeriffSession();
                }
                currentSessionUrlRef.current = undefined;
                await refreshUser?.();
                // this will go back to the PreIdVerificationScreen
                navigation.goBack();
            } else {
                currentSessionUrlRef.current = undefined;
                // this will go back to the PreIdVerificationScreen
                navigation.goBack();
            }
        },
        [
            lang,
            colors.background,
            colors.primary,
            colors.secondary,
            colors.accentLighter,
            endVeriffSession,
            refreshUser,
            navigation,
            resetVeriffSession,
            trackEvent,
        ],
    );

    useEffect(() => {
        if (currentUrl && currentSessionUrlRef.current !== currentUrl) {
            currentSessionUrlRef.current = currentUrl;
            void trackEvent("kyc_started");
            void doVerification(currentUrl);
        }
    }, [currentUrl, doVerification, trackEvent]);

    return (
        <View style={{ flex: 1 }}>
            {error && <ScreenCardHeadband mode="error" text={error.message} />}
            {!currentUrl && (
                <ScreenCardHeadband mode="error" text={i18n.t("veriff.generic_error")} />
            )}
            {loading && <Loading />}
        </View>
    );
};

export default VeriffScreen;
